const COOKIE_NAME = 'visitor_id';
const MAX_AGE = 60 * 60 * 24 * 365; // 1 anno in secondi
const DOMAIN = '.escortfun.net';

function getCookie(name: string): string | null {
  const match = document.cookie
    .split('; ')
    .find(row => row.startsWith(name + '='));
  return match ? decodeURIComponent(match.split('=')[1]) : null;
}

function setCookie(name: string, value: string): void {
  document.cookie = `${name}=${encodeURIComponent(value)}; max-age=${MAX_AGE}; path=/; domain=${DOMAIN}; SameSite=Lax`;
}

/**
 * Restituisce il visitor_id dell'utente corrente.
 * Se non esiste, ne genera uno nuovo (primo accesso).
 * isReturning è true se il cookie era già presente.
 */
export function getOrCreateVisitorId(): { visitorId: string; isReturning: boolean } {
  const existing = getCookie(COOKIE_NAME);
  if (existing) {
    return { visitorId: existing, isReturning: true };
  }

  const newId = crypto.randomUUID();
  setCookie(COOKIE_NAME, newId);
  return { visitorId: newId, isReturning: false };
}

export function getVisitorId(): string | null {
  return getCookie(COOKIE_NAME);
}

const SESSION_ENDPOINT  = 'https://img.escortfun.net/cache/session.php';
const REFERRAL_ENDPOINT = 'https://img.escortfun.net/cache/referral.php';
const SESSION_KEY = '_ref_tracked';

/**
 * Invia il referral source al backend (una sola volta per sessione).
 * Cattura: document.referrer, landing page, UTM params, visitor_id.
 */
export function trackReferral(): void {
  if (sessionStorage.getItem(SESSION_KEY)) return;
  sessionStorage.setItem(SESSION_KEY, '1');

  const vid = getVisitorId() ?? '';
  const ref = document.referrer;
  const land = window.location.pathname;
  const search = new URLSearchParams(window.location.search);

  const params = new URLSearchParams();
  if (vid) params.set('vid', vid);
  if (ref) params.set('ref', ref);
  params.set('land', land);
  const utmSource   = search.get('utm_source');
  const utmMedium   = search.get('utm_medium');
  const utmCampaign = search.get('utm_campaign');
  if (utmSource)   params.set('utm_source', utmSource);
  if (utmMedium)   params.set('utm_medium', utmMedium);
  if (utmCampaign) params.set('utm_campaign', utmCampaign);

  const url = `${REFERRAL_ENDPOINT}?${params.toString()}`;
  try {
    navigator.sendBeacon(url);
  } catch (_) {
    fetch(url).catch(() => {});
  }
}

/**
 * Avvia il tracking del tempo sul sito.
 * Invia la durata (in secondi) al backend quando l'utente lascia la pagina.
 * Usa visibilitychange (mobile) + pagehide (desktop) con flag anti-doppio invio.
 */
export function initSessionTracking(): void {
  if (sessionStorage.getItem('_sess_init')) return;
  sessionStorage.setItem('_sess_init', '1');
  sessionStorage.setItem('_sess_start', Date.now().toString());
  sessionStorage.setItem('_sess_land', window.location.pathname);

  let sent = false;

  const send = () => {
    if (sent) return;
    const start = parseInt(sessionStorage.getItem('_sess_start') ?? '0', 10);
    if (!start) return;
    const duration = Math.round((Date.now() - start) / 1000);
    if (duration < 3) return; // ignora bounce < 3s

    const vid  = getVisitorId() ?? '';
    const land = sessionStorage.getItem('_sess_land') ?? '/';
    const params = new URLSearchParams();
    if (vid) params.set('vid', vid);
    params.set('d', duration.toString());
    params.set('land', land);

    const ok = navigator.sendBeacon(`${SESSION_ENDPOINT}?${params.toString()}`);
    if (ok) sent = true;
  };

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') send();
  });
  window.addEventListener('pagehide', send);
}
